import type { FoilConfig, FoilTexture, FoilTint } from "types/cards/foil";

export type { FoilConfig, FoilTexture, FoilTint };

const FOIL_DIR = "/assets/cards/foil";

export const FOIL_TEXTURES: readonly FoilTexture[] = [
  "illusion",
  "metal",
  "rainbow",
  "grain",
  "lines",
  "stars",
];

const sheenFor = (stops: string): string =>
  `linear-gradient(115deg, transparent 15%, ${stops}, transparent 85%)`;

export const FOIL_TINTS: readonly FoilTint[] = [
  "rainbow",
  "aqua",
  "ember",
  "mono",
  "violet",
  "rose",
  "lime",
  "gold",
  "ice",
  "toxic",
  "void",
];

/** Moving color sheen — the main readable "foil" feel. */
export const FOIL_SHEEN: Record<FoilTint, string> = {
  rainbow: sheenFor(
    "rgba(255,0,132,0.45) 33%, rgba(0,255,255,0.45) 44%, rgba(0,255,140,0.35) 52%, rgba(255,255,0,0.4) 60%, rgba(255,0,132,0.45) 71%",
  ),
  aqua: sheenFor(
    "rgba(0,180,255,0.42) 35%, rgba(0,255,200,0.42) 50%, rgba(80,255,120,0.35) 65%",
  ),
  ember: sheenFor(
    "rgba(255,60,40,0.42) 35%, rgba(255,150,20,0.42) 50%, rgba(255,220,60,0.35) 65%",
  ),
  mono: sheenFor(
    "rgba(255,255,255,0.4) 35%, rgba(160,170,190,0.35) 50%, rgba(255,255,255,0.4) 65%",
  ),
  violet: sheenFor(
    "rgba(160,80,255,0.45) 35%, rgba(220,120,255,0.4) 50%, rgba(100,60,220,0.4) 65%",
  ),
  rose: sheenFor(
    "rgba(255,80,140,0.45) 35%, rgba(255,140,180,0.4) 50%, rgba(255,60,100,0.4) 65%",
  ),
  lime: sheenFor(
    "rgba(140,255,40,0.42) 35%, rgba(200,255,80,0.4) 50%, rgba(80,220,60,0.38) 65%",
  ),
  gold: sheenFor(
    "rgba(255,200,60,0.45) 35%, rgba(255,230,120,0.42) 50%, rgba(220,160,40,0.4) 65%",
  ),
  ice: sheenFor(
    "rgba(180,230,255,0.45) 35%, rgba(220,245,255,0.4) 50%, rgba(140,200,255,0.4) 65%",
  ),
  toxic: sheenFor(
    "rgba(180,255,40,0.4) 35%, rgba(120,60,200,0.42) 50%, rgba(60,255,140,0.38) 65%",
  ),
  // Black chrome: dark body + narrow silver specular so it still reads as foil.
  void: sheenFor(
    "rgba(0,0,0,0.55) 36%, rgba(28,28,34,0.5) 44%, rgba(210,218,230,0.55) 50%, rgba(18,18,22,0.52) 56%, rgba(0,0,0,0.5) 64%",
  ),
};

export const FOIL_TEXTURE_URLS = {
  glitter: `${FOIL_DIR}/glitter.png`,
  illusion: `${FOIL_DIR}/illusion.png`,
  metal: `${FOIL_DIR}/metal.png`,
  rainbow: `${FOIL_DIR}/rainbow.jpg`,
  grain: `${FOIL_DIR}/grain.webp`,
} as const;

/** Pattern-only (no sheen). Opacity is controlled by FoilLayer — keep styles neutral. */
export const foilPatternStyle = (texture: FoilTexture): React.CSSProperties => {
  switch (texture) {
    case "illusion":
      return {
        backgroundImage: `url(${FOIL_TEXTURE_URLS.illusion})`,
        backgroundSize: "90% 90%",
        backgroundPosition: "center",
        mixBlendMode: "soft-light",
      };
    case "metal":
      return {
        backgroundImage: `url(${FOIL_TEXTURE_URLS.metal})`,
        backgroundSize: "70% 70%",
        backgroundPosition: "var(--mx, 50%) var(--my, 50%)",
        mixBlendMode: "soft-light",
      };
    case "rainbow":
      return {
        backgroundImage: `url(${FOIL_TEXTURE_URLS.rainbow})`,
        backgroundSize: "140% 140%",
        backgroundPosition: "var(--mx, 50%) var(--my, 50%)",
        mixBlendMode: "soft-light",
      };
    case "grain":
      return {
        backgroundImage: `url(${FOIL_TEXTURE_URLS.grain})`,
        backgroundSize: "50% 50%",
        backgroundPosition: "center",
        mixBlendMode: "soft-light",
      };
    case "lines":
      return {
        backgroundImage:
          "repeating-linear-gradient(125deg, rgba(255,255,255,0.35) 0px, rgba(255,255,255,0.35) 1.5px, transparent 1.5px, transparent 7px)",
        mixBlendMode: "soft-light",
      };
    case "stars":
      return {
        backgroundImage: `
          radial-gradient(circle, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.25) 10%, transparent 42%),
          radial-gradient(circle, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.25) 10%, transparent 42%)
        `,
        backgroundSize: "20px 20px, 30px 30px",
        backgroundPosition: "0 0, 11px 15px",
        backgroundRepeat: "repeat, repeat",
        mixBlendMode: "soft-light",
      };
  }
};

/** Deterministic pseudo-random texture pick so each card gets a stable but varied holo pattern. */
export const pickFoilTexture = (seed: string): FoilTexture => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return FOIL_TEXTURES[Math.abs(hash) % FOIL_TEXTURES.length]!;
};
